import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LayoutContext } from '../../contexts/layout.context';
import { authSelectors } from '../../store/selectors/auth';
import { profileSelectors } from '../../store/selectors/profile';
import { getToken } from '../../utils/token';
import { saveTrainingResult, wordGame } from '../../api/training';
import useSpeech from '../../hooks/useSpeech';
import { initSound, isMuted, playSfx, unlockAudio } from '../../utils/sounds';
import SoundToggle from '../../common/SoundToggle/SoundToggle';
import SpeakButton from '../../common/SpeakButton/SpeakButton';
import Spinner from '../../common/Spinner/Spinner';
import { Confetti, TrainingToasts, announceAchievements, useToasts } from '../../components/Training/TrainingFx';
import './word-game.scss';

const BLITZ_TENTHS = 600;
const PENALTY_TENTHS = 30;
const PAIRS_ROUNDS = 2;
const PAIRS_SIZE = 5;
const PAIRS_PENALTY = 2;
const RING = 2 * Math.PI * 46;
const WORDS_PREVIEW = 12;

const norm = (value) => String(value || '').trim().toLowerCase();

const shuffle = (list) => {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const pickDistinct = (words, count, except = null) => {
  const seenWord = new Set(except ? [norm(except.word)] : []);
  const seenTranslation = new Set(except ? [norm(except.translation)] : []);
  const result = [];
  shuffle(words).forEach((word) => {
    if (result.length >= count || (except && word.id === except.id)) return;
    if (seenWord.has(norm(word.word)) || seenTranslation.has(norm(word.translation))) return;
    seenWord.add(norm(word.word));
    seenTranslation.add(norm(word.translation));
    result.push(word);
  });
  return result;
};

const makeQuestion = (words, lastId) => {
  const pool = words.length > 1 ? words.filter((word) => word.id !== lastId) : words;
  const word = pool[Math.floor(Math.random() * pool.length)];
  return {
    key: `${word.id}-${Date.now()}`,
    word,
    reverse: Math.random() < 0.3,
    options: shuffle([word, ...pickDistinct(words, 2, word)]),
    picked: null,
  };
};

const makeBoard = (words) => {
  const set = pickDistinct(words, PAIRS_SIZE);
  return { left: shuffle(set), right: shuffle(set), done: [], bad: null };
};

const plural = (n, one, few, many) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};

const readSavedUserId = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.id;
  } catch (e) {
    return undefined;
  }
};

const WordPic = ({ word }) => {
  const [failed, setFailed] = useState(false);
  if (!word.image && word.emoji) {
    return <span className="wg-dict__pic wg-dict__pic--emoji" aria-hidden="true">{word.emoji}</span>;
  }
  if (!word.image || failed) {
    return <span className="wg-dict__pic wg-dict__pic--empty" aria-hidden="true">{word.word.charAt(0).toUpperCase()}</span>;
  }
  return <img className="wg-dict__pic" src={word.image} alt="" loading="lazy" onError={() => setFailed(true)} />;
};

WordPic.propTypes = {
  word: PropTypes.shape({
    word: PropTypes.string.isRequired,
    image: PropTypes.string,
    emoji: PropTypes.string,
  }).isRequired,
};

const Games = ({ data, token, onExit }) => {
  const speech = useSpeech(data.tts_lang);
  const words = data.words;
  const [stage, setStage] = useState('hub');
  const [best, setBest] = useState(data.best || { blitz: 0, pairs: 0 });
  const [achievements, setAchievements] = useState(data.achievements || []);
  const [showAll, setShowAll] = useState(false);
  const [result, setResult] = useState(null);
  const [burst, setBurst] = useState(null);
  const [toasts, pushToast] = useToasts();
  const [, force] = useReducer((x) => x + 1, 0);
  const game = useRef(null);
  const finishRef = useRef(() => {});
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach((timer) => clearTimeout(timer)), []);

  const later = (fn, ms) => {
    timers.current.push(setTimeout(fn, ms));
  };

  const say = (text) => {
    if (!isMuted() && text) speech.speakText(text, { id: 'word-game' });
  };

  const celebrate = (count) => setBurst({ id: Date.now(), count });

  const submit = (payload) => {
    saveTrainingResult({ token, result: { lang: data.lang, ...payload } })
      .then((res) => {
        const saved = res.data.data;
        setBest(saved.best);
        setAchievements(saved.achievements);
        announceAchievements(saved.new, pushToast);
      })
      .catch(() => pushToast('Не вдалося зберегти результат. Перевірте інтернет.', 'red'));
  };

  const startBlitz = () => {
    unlockAudio();
    playSfx('tap');
    const question = makeQuestion(words, null);
    game.current = {
      kind: 'blitz',
      tenths: BLITZ_TENTHS,
      lastSec: BLITZ_TENTHS / 10,
      score: 0,
      combo: 0,
      maxCombo: 0,
      mult: 1,
      multPop: 0,
      right: 0,
      answered: 0,
      lock: false,
      over: false,
      floaty: null,
      question,
    };
    setStage('blitz');
    if (!question.reverse) say(question.word.word);
  };

  const answerBlitz = (option) => {
    const g = game.current;
    if (!g || g.kind !== 'blitz' || g.lock || g.over) return;
    g.lock = true;
    g.answered += 1;
    const isRight = option.id === g.question.word.id;
    g.question = { ...g.question, picked: option.id };

    if (isRight) {
      const previous = g.mult;
      g.right += 1;
      g.combo += 1;
      g.maxCombo = Math.max(g.maxCombo, g.combo);
      g.mult = g.combo >= 10 ? 3 : (g.combo >= 5 ? 2 : 1);
      g.score += g.mult;
      g.floaty = { id: Date.now(), text: `+${g.mult}`, kind: 'ok' };
      playSfx('ok');
      if (g.mult > previous) {
        g.multPop += 1;
        playSfx('combo');
      }
    } else {
      g.combo = 0;
      g.mult = 1;
      g.tenths = Math.max(0, g.tenths - PENALTY_TENTHS);
      g.floaty = { id: Date.now(), text: '−3 с', kind: 'bad' };
      playSfx('bad');
    }
    force();

    later(() => {
      if (game.current !== g || g.over) return;
      g.lock = false;
      g.question = makeQuestion(words, g.question.word.id);
      if (!g.question.reverse) say(g.question.word.word);
      force();
    }, isRight ? 260 : 650);
  };

  const finishBlitz = () => {
    const g = game.current;
    const isRecord = g.score > (best.blitz || 0);
    const accuracy = g.answered ? Math.round((g.right / g.answered) * 100) : 0;
    setResult({ game: 'blitz', isRecord, score: g.score, right: g.right, answered: g.answered });
    setStage('result');
    celebrate(isRecord ? 200 : 90);
    playSfx(isRecord ? 'win' : 'up');
    if (isRecord) setBest((prev) => ({ ...prev, blitz: g.score }));
    submit({
      game: 'blitz',
      score: g.score,
      correct: g.right,
      total: g.answered,
      max_combo: g.maxCombo,
      seconds: BLITZ_TENTHS / 10,
      accuracy,
    });
  };

  const startPairs = () => {
    unlockAudio();
    playSfx('tap');
    game.current = {
      kind: 'pairs',
      round: 0,
      penalty: 0,
      errors: 0,
      matched: 0,
      startedAt: Date.now(),
      selA: null,
      selB: null,
      over: false,
      board: makeBoard(words),
    };
    setStage('pairs');
  };

  const finishPairs = () => {
    const g = game.current;
    g.over = true;
    const seconds = Math.max(1, Math.round((Date.now() - g.startedAt) / 1000 + g.penalty));
    const isRecord = !best.pairs || seconds < best.pairs;
    setResult({ game: 'pairs', isRecord, seconds, penalty: g.penalty, pairs: g.matched });
    setStage('result');
    celebrate(isRecord ? 160 : 80);
    playSfx('win');
    if (isRecord) setBest((prev) => ({ ...prev, pairs: seconds }));
    submit({
      game: 'pairs',
      score: g.matched,
      correct: g.matched,
      total: g.matched + g.errors,
      max_combo: 0,
      seconds,
      accuracy: g.matched ? Math.round((g.matched / (g.matched + g.errors)) * 100) : 0,
    });
  };

  const tapPair = (side, word) => {
    const g = game.current;
    if (!g || g.kind !== 'pairs' || g.over || g.board.done.includes(word.id)) return;
    playSfx('tap');
    if (side === 'a') {
      g.selA = word.id;
      say(word.word);
    } else {
      g.selB = word.id;
    }

    if (g.selA !== null && g.selB !== null) {
      const left = g.selA;
      const right = g.selB;
      g.selA = null;
      g.selB = null;
      if (left === right) {
        g.board.done = [...g.board.done, left];
        g.matched += 1;
        playSfx('ok');
        if (g.board.done.length === g.board.left.length) {
          const board = g.board;
          later(() => {
            if (game.current !== g || g.board !== board) return;
            g.round += 1;
            if (g.round < PAIRS_ROUNDS) {
              g.board = makeBoard(words);
              force();
            } else {
              finishPairs();
            }
          }, 450);
        }
      } else {
        g.errors += 1;
        g.penalty += PAIRS_PENALTY;
        const bad = { left, right, id: Date.now() };
        g.board.bad = bad;
        playSfx('bad');
        later(() => {
          if (game.current === g && g.board.bad === bad) {
            g.board.bad = null;
            force();
          }
        }, 450);
      }
    }
    force();
  };

  finishRef.current = finishBlitz;

  useEffect(() => {
    if (stage !== 'blitz' && stage !== 'pairs') return undefined;
    const id = setInterval(() => {
      const g = game.current;
      if (!g || g.over) return;
      if (g.kind === 'blitz') {
        g.tenths = Math.max(0, g.tenths - 1);
        const sec = Math.ceil(g.tenths / 10);
        if (sec !== g.lastSec) {
          g.lastSec = sec;
          if (sec <= 5 && sec > 0) playSfx('tick');
        }
        if (g.tenths <= 0) {
          g.over = true;
          finishRef.current();
          return;
        }
      }
      force();
    }, 100);
    return () => clearInterval(id);
  }, [stage]);

  const exitGame = () => {
    if (game.current) game.current.over = true;
    game.current = null;
    setStage('hub');
  };

  const showHint = (item) => {
    playSfx('tap');
    pushToast(item.unlocked ? `${item.icon} ${item.title} — уже ваше!` : `🔒 ${item.hint}`, item.unlocked ? 'gold' : 'green');
  };

  const unlockedCount = achievements.filter((item) => item.unlocked).length;
  const fx = (
    <>
      <Confetti burst={burst} />
      <TrainingToasts toasts={toasts} />
    </>
  );

  if (stage === 'blitz' && game.current) {
    const g = game.current;
    const q = g.question;
    const seconds = Math.ceil(g.tenths / 10);
    const hurry = g.tenths <= 100;
    return (
      <div className="wg container">
        {fx}
        <div className="wg-top">
          <button type="button" className="wg-back" onClick={exitGame} aria-label="Завершити гру">×</button>
          <span className="wg-kind">⚡ Бліц 60 секунд</span>
          <SoundToggle />
        </div>
        <div className="wg-blitz">
          <div className="wg-blitz__score"><b>{g.score}</b><span>бали</span></div>
          <div className={`wg-ring ${hurry ? 'is-hurry' : ''}`.trim()}>
            <svg viewBox="0 0 108 108" aria-hidden="true">
              <circle className="wg-ring__bg" cx="54" cy="54" r="46" />
              <circle className="wg-ring__fg" cx="54" cy="54" r="46" strokeDasharray={RING} strokeDashoffset={RING * (1 - g.tenths / BLITZ_TENTHS)} />
            </svg>
            <b>{seconds}</b>
            {g.floaty && <span key={g.floaty.id} className={`wg-floaty is-${g.floaty.kind}`}>{g.floaty.text}</span>}
          </div>
          <div className="wg-blitz__score wg-blitz__score--right">
            <span key={g.multPop} className={`wg-mult ${g.multPop ? 'is-pop' : ''}`.trim()}>×{g.mult}</span>
            <span>множник</span>
          </div>
        </div>

        <div key={q.key} className={`wg-q ${q.picked !== null ? (q.picked === q.word.id ? 'is-ok' : 'is-bad') : ''}`.trim()}>
          {q.reverse ? (
            <>
              {q.word.image && <img className="wg-q__pic" src={q.word.image} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
              {!q.word.image && q.word.emoji && <span className="wg-q__pic wg-q__pic--emoji" aria-hidden="true">{q.word.emoji}</span>}
              <b className="wg-q__ua">{q.word.translation}</b>
            </>
          ) : (
            <>
              <b className="wg-q__word">{q.word.word}</b>
              {q.word.transcription && <small>{q.word.transcription}</small>}
            </>
          )}
        </div>
        <div className="wg-options">
          {q.options.map((option, index) => {
            const isAnswer = option.id === q.word.id;
            const isPicked = q.picked === option.id;
            const cls = q.picked !== null ? (isAnswer ? 'is-ok' : (isPicked ? 'is-bad' : '')) : '';
            return (
              <button key={option.id} type="button" className={`wg-opt ${cls}`.trim()} onClick={() => answerBlitz(option)} disabled={g.lock}>
                <span className="wg-opt__num">{index + 1}</span>
                <span className="wg-opt__text">{q.reverse ? option.word : option.translation}</span>
                {q.picked !== null && isAnswer && <span className="wg-opt__mark" aria-label="правильно">✓</span>}
                {q.picked !== null && isPicked && !isAnswer && <span className="wg-opt__mark" aria-label="помилка">✕</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (stage === 'pairs' && game.current) {
    const g = game.current;
    const elapsed = (Date.now() - g.startedAt) / 1000 + g.penalty;
    const total = PAIRS_ROUNDS * g.board.left.length;
    const progress = Math.round(((g.round * g.board.left.length + g.board.done.length) / total) * 100);
    const cls = (side, word) => {
      const list = ['wg-pair'];
      if (g.board.done.includes(word.id)) list.push('is-gone');
      else if ((side === 'a' ? g.selA : g.selB) === word.id) list.push('is-sel');
      if (g.board.bad && (side === 'a' ? g.board.bad.left : g.board.bad.right) === word.id) list.push('is-bad');
      return list.join(' ');
    };
    return (
      <div className="wg container">
        {fx}
        <div className="wg-top">
          <button type="button" className="wg-back" onClick={exitGame} aria-label="Завершити гру">×</button>
          <div className="wg-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
            <span style={{ width: `${progress}%` }} />
          </div>
          <span className="wg-watch">{elapsed.toFixed(1)}с</span>
          <SoundToggle />
        </div>
        <div className="wg-card" key={`${g.round}`}>
          <div className="wg-card__kind">🧩 Пари на час · раунд {g.round + 1}/{PAIRS_ROUNDS}</div>
          <p className="wg-card__q">Знайдіть пари</p>
          <div className="wg-pairs">
            <div className="wg-pairs__col">
              {g.board.left.map((word) => (
                <button key={word.id} type="button" className={cls('a', word)} onClick={() => tapPair('a', word)}>
                  {word.word}
                </button>
              ))}
            </div>
            <div className="wg-pairs__col">
              {g.board.right.map((word) => (
                <button key={word.id} type="button" className={cls('b', word)} onClick={() => tapPair('b', word)}>
                  {word.translation}
                </button>
              ))}
            </div>
          </div>
          <p className="wg-card__hint">Помилка додає {PAIRS_PENALTY} секунди</p>
        </div>
      </div>
    );
  }

  if (stage === 'result' && result) {
    const isBlitz = result.game === 'blitz';
    return (
      <div className="wg container">
        {fx}
        <div className="wg-card wg-card--center wg-result">
          <div className="wg-emoji wg-emoji--jump" aria-hidden="true">{result.isRecord ? '🏆' : (isBlitz ? '⚡' : '⏱️')}</div>
          <h1 className="wg-title">
            {isBlitz && (result.isRecord ? 'Новий рекорд! 🏆' : 'Час вийшов!')}
            {!isBlitz && (result.isRecord ? 'Новий рекорд! ⏱️' : 'Готово!')}
          </h1>
          <p className="wg-text">
            {isBlitz && (result.isRecord ? 'Ви перевершили свій найкращий результат.' : `Рекорд: ${best.blitz}. Ще трохи — і поб’єте!`)}
            {!isBlitz && `${result.pairs} ${plural(result.pairs, 'пара', 'пари', 'пар')} за ${result.seconds} с${result.penalty ? `, з них ${result.penalty} с штрафу` : ''}.`}
          </p>
          <div className="wg-stats">
            {isBlitz ? (
              <>
                <div className="wg-stat wg-stat--y"><b>{result.score}</b><span>бали</span></div>
                <div className="wg-stat wg-stat--g"><b>{result.right}/{result.answered}</b><span>правильно</span></div>
                <div className="wg-stat wg-stat--o"><b>{best.blitz}</b><span>рекорд</span></div>
              </>
            ) : (
              <>
                <div className="wg-stat wg-stat--y"><b>{result.seconds}с</b><span>час</span></div>
                <div className="wg-stat wg-stat--g"><b>{best.pairs}с</b><span>рекорд</span></div>
                <div className="wg-stat wg-stat--o"><b>{result.penalty}с</b><span>штраф</span></div>
              </>
            )}
          </div>
          <div className="wg-actions">
            <button type="button" className="wg-btn wg-btn--light" onClick={() => setStage('hub')}>Готово</button>
            <button type="button" className="wg-btn wg-btn--main" onClick={isBlitz ? startBlitz : startPairs}>Ще раз</button>
          </div>
        </div>
      </div>
    );
  }

  const shown = showAll ? words : words.slice(0, WORDS_PREVIEW);

  return (
    <div className="wg container">
      {fx}
      <div className="wg-top">
        <button type="button" className="wg-back" onClick={onExit} aria-label="Назад">‹</button>
        <span className="wg-kind">Тренування</span>
        <SoundToggle />
      </div>
      <h1 className="wg-title wg-title--page">Вивчення слів</h1>
      <p className="wg-text">{words.length} {plural(words.length, 'слово', 'слова', 'слів')} з уроків, які ви вже пройшли</p>

      <div className="wg-chal">
        <svg className="wg-chal__timer" viewBox="0 0 80 80" aria-hidden="true">
          <circle className="wg-chal__timer-bg" cx="40" cy="40" r="32" />
          <circle className="wg-chal__timer-fg" cx="40" cy="40" r="32" />
          <text x="40" y="48" textAnchor="middle">60</text>
        </svg>
        <h2>Бліц<br />60 секунд</h2>
        <p>Скільки слів встигнете перекласти за хвилину? 5 правильних поспіль — бали ×2, 10 — ×3. Помилка забирає 3 секунди.</p>
        <div className="wg-chal__best">
          <span>🏆 Рекорд: {best.blitz || 0}</span>
          <span>📚 Слів у грі: {words.length}</span>
        </div>
        <button type="button" className="wg-btn wg-btn--white" onClick={startBlitz}>Грати</button>
      </div>

      <button type="button" className="wg-tcard" onClick={startPairs}>
        <span className="wg-tcard__ic" aria-hidden="true">🧩</span>
        <span className="wg-tcard__main">
          <b>Пари на час</b>
          <span>{PAIRS_ROUNDS} раунди по {PAIRS_SIZE} пар. Рекорд: {best.pairs ? `${best.pairs} с` : 'ще немає'}</span>
        </span>
        <span className="wg-tcard__go" aria-hidden="true">›</span>
      </button>

      <h2 className="wg-sec">Досягнення <span className="wg-chip">{unlockedCount}/{achievements.length}</span></h2>
      <div className="wg-badges">
        {achievements.map((item) => (
          <button key={item.id} type="button" className={`wg-badge ${item.unlocked ? '' : 'is-lock'}`.trim()} onClick={() => showHint(item)}>
            <span className="wg-badge__ic"><span>{item.icon}</span></span>
            {item.title}
          </button>
        ))}
      </div>

      <h2 className="wg-sec">Ваші слова <span className="wg-chip">{words.length}</span></h2>
      <div className="wg-dict">
        {shown.map((word) => (
          <div key={word.id} className="wg-dict__it">
            <WordPic word={word} />
            <span className="wg-dict__main">
              <b>{word.word}</b>
              <span>{word.transcription ? `${word.transcription} · ` : ''}{word.translation}</span>
              {word.example && (
                <span className="wg-dict__ex">
                  <i>{word.example.sentence}</i>
                  {word.example.translation && <span>{word.example.translation}</span>}
                </span>
              )}
            </span>
            <SpeakButton text={word.word} lang={data.tts_lang} className="wg-dict__spk" />
          </div>
        ))}
      </div>
      {words.length > WORDS_PREVIEW && (
        <button type="button" className="wg-btn wg-btn--light wg-more" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Згорнути' : `Показати всі (${words.length})`}
        </button>
      )}
    </div>
  );
};

Games.propTypes = {
  data: PropTypes.shape({
    lang: PropTypes.string,
    tts_lang: PropTypes.string.isRequired,
    words: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      word: PropTypes.string.isRequired,
      translation: PropTypes.string.isRequired,
      transcription: PropTypes.string,
      image: PropTypes.string,
      emoji: PropTypes.string,
      example: PropTypes.shape({
        sentence: PropTypes.string,
        translation: PropTypes.string,
      }),
    })).isRequired,
    best: PropTypes.shape({ blitz: PropTypes.number, pairs: PropTypes.number }),
    achievements: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      hint: PropTypes.string.isRequired,
      unlocked: PropTypes.bool.isRequired,
    })),
  }).isRequired,
  token: PropTypes.string.isRequired,
  onExit: PropTypes.func.isRequired,
};

const WordGame = () => {
  const navigate = useNavigate();
  const { setLayout } = useContext(LayoutContext);
  const auth = useSelector(authSelectors);
  const profile = useSelector(profileSelectors);
  const token = auth.token;
  const studentId = profile?.user?.id || readSavedUserId();
  const [state, setState] = useState({ load: true, error: '', data: null });

  getToken();

  useEffect(() => {
    document.title = 'Вивчення слів | New Brain';
    setLayout({ page: 'Вивчення слів' });
  }, [setLayout]);

  useEffect(() => {
    initSound(studentId);
  }, [studentId]);

  const load = useCallback(() => {
    if (!token) return;
    setState({ load: true, error: '', data: null });
    wordGame({ token })
      .then((res) => setState({ load: false, error: '', data: res.data.data }))
      .catch((error) => {
        setState({ load: false, error: error?.response?.data?.info?.message || 'Не вдалося завантажити слова. Спробуйте ще раз.', data: null });
      });
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const back = () => navigate('/homework');

  if (state.load) {
    return <Spinner />;
  }

  if (state.error || !state.data?.available) {
    return (
      <div className="wg container">
        <div className="wg-card wg-card--center">
          <div className="wg-emoji" aria-hidden="true">⚡</div>
          <h1 className="wg-title">Вивчення слів</h1>
          <p className="wg-text">{state.error || state.data?.message}</p>
          <button type="button" className="wg-btn wg-btn--light" onClick={state.error ? load : back}>
            {state.error ? 'Спробувати ще раз' : 'Назад'}
          </button>
        </div>
      </div>
    );
  }

  return <Games data={state.data} token={token} onExit={back} />;
};

export default WordGame;
